import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "@stores/root-store";
import { useInviteModal } from "@components/ModalContext/InviteModalContext";
import UserDetails from "@components/layout/superadmin-layout/pages/userDetails/UserDetails";
import { useUserContext } from "@components/layout/superadmin-layout/SuperAdminLayout";
import InviteModal from "@components/layout/admin-layout/components/inviteModal/InviteModal";
import DeleteUserModal from "@components/layout/admin-layout/components/deleteUserModal/DeleteUserModal";
import DataGrid from "@components/shared/DataGrid/DataGrid";
import {
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import DeleteIcon from "@assets/componentIcons/DeleteIcon";
import updateIcon from "../../../../../assets/icons/Icon-up.svg";
import updateIconDown from "../../../../../assets/icons/Icon-down.svg";
import ChangeUserPlanModal from "@components/layout/superadmin-layout/changeUserPlanModal/ChangeUserPlanModal";
import ConfirmationModal from "@components/layout/superadmin-layout/confirmationModal/ConfirmationModal";
import { constRoute } from "@utils/route";

const Users = () => {
  const { isModalOpen } = useInviteModal();
  const [userEmailToDelete, setUserEmailToDelete] = useState<string | null>(
    null,
  );
  const [userToUpdate, setUserToUpdate] = useState<string | null>(null);
  const [typeToUpdate, setTypeToUpdate] = useState("Basic");
  const [openUpdatePlanModal, setOpenUpdatePlanModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [currentUserType, setCurrentUserType] = useState("");
  const [isUpgrade, setIsUpgrade] = useState<boolean | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email");

  const {
    user: {
      getAllUsers: allUsers,
      onDeleteUser,
      setAllUsers,
      isLoadingMasterAdmin,
      onUpdateUserPlan,
      onDowngradeUserPlan,
    },
  } = useStore(null);

  const { userDetails, updateUserDetails } = useUserContext();

  const usersForSelect = useMemo(() => {
    return (
      allUsers?.["account holders"]?.map((el) => ({
        label: `${el.account_holder} - ${el.user_email}`,
        value: `${el.account_holder} - ${el.user_email}`,
      })) || []
    );
  }, [allUsers]);

  const handleUserDelete = async (email: string) => {
    const updatedUsers =
      allUsers?.["account holders"]?.filter((el) => el.user_email !== email) ||
      [];

    onDeleteUser(email);
    setAllUsers({ ...allUsers, ["account holders"]: updatedUsers });
  };

  useEffect(() => {
    const email = userToUpdate?.split("-")[1]?.trim();
    const user = allUsers?.["account holders"]?.find(
      (el) => el.user_email === email,
    );
    if (user) setCurrentUserType(user.plan);
  }, [userToUpdate, allUsers]);

  // Reset all modals
  const resetAllModals = () => {
    setOpenUpdatePlanModal(false);
    setOpenConfirmationModal(false);
    setOpenDeleteModal(false);
    setUserToUpdate(null);
    setIsUpgrade(null);
    setUserEmailToDelete(null);
  };

  // Handle upgrade action
  const handleUpgrade = (e: React.MouseEvent, params: GridRenderCellParams) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset all modals first
    resetAllModals();

    // Set upgrade specific states
    setCurrentUserType(params.row.plan);
    setIsUpgrade(true);
    setUserToUpdate(`${params.row.account_holder} - ${params.row.user_email}`);
    setTypeToUpdate(params.row.plan); // Start with current plan
    setOpenUpdatePlanModal(true);
  };

  // Handle downgrade action
  const handleDowngrade = (
    e: React.MouseEvent,
    params: GridRenderCellParams,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset all modals first
    resetAllModals();

    // Set downgrade specific states
    setCurrentUserType(params.row.plan);
    setIsUpgrade(false);
    setUserToUpdate(`${params.row.account_holder} - ${params.row.user_email}`);
    setTypeToUpdate(params.row.plan); // Start with current plan
    setOpenUpdatePlanModal(true);
  };

  // Handle delete action
  const handleDelete = (e: React.MouseEvent, params: GridRenderCellParams) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset all modals first
    resetAllModals();

    // Set delete specific states
    const userEmail = params.row.user_email;
    const accountHolder = params.row.account_holder;

    setUserEmailToDelete(userEmail);
    setOpenDeleteModal(true);
  };

  // Handle pagination changes
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const columns: GridColDef[] = [
    {
      field: "account_holder",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            cursor: "pointer",
            py: 0.5,
            width: "100%",
          }}
          onClick={() =>
            navigate(
              `${constRoute.masteradminUsers}?email=${encodeURIComponent(
                params.row.user_email,
              )}`,
            )
          }
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: "#000",
              borderBottom: "1px solid #000",
              display: "inline-block",
              lineHeight: 1.2,
              padding: 0,
              margin: 0,
              width: "fit-content",
            }}
          >
            {params.row.account_holder}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.8rem" }}
          >
            {params.row.user_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "plan",
      headerName: "Plan",
      flex: 1,
    },
    {
      field: "entity_type",
      headerName: "Entity Type",
      flex: 1,
    },
    {
      field: "credits",
      headerName: "Credits used / Credits purchased",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        // Use the field that actually exists in the data
        const creditsField = params.row["Credits used / Credits purchased"];

        if (creditsField) {
          return creditsField;
        }

        // Fallback if the field doesn't exist
        return "N/A";
      },
    },
    {
      field: "last_logged_in",
      headerName: "Last Login",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Upgrade Icon */}
            <IconButton
              size="small"
              onClick={(e) => {
                handleUpgrade(e, params);
              }}
              sx={{
                padding: "4px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(255, 152, 0, 0.04)",
                },
              }}
              title="Upgrade plan"
              data-testid="upgrade-button"
            >
              <img
                src={updateIcon}
                alt="upgrade plan"
                style={{
                  width: "16px",
                  height: "16px",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            </IconButton>

            {/* Downgrade Icon */}
            <IconButton
              size="small"
              onClick={(e) => {
                handleDowngrade(e, params);
              }}
              sx={{
                padding: "4px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
              }}
              title="Downgrade plan"
              data-testid="downgrade-button"
            >
              <img
                src={updateIconDown}
                alt="downgrade plan"
                style={{
                  width: "16px",
                  height: "16px",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            </IconButton>

            {/* Delete Icon */}
            <IconButton
              size="small"
              onClick={(e) => {
                handleDelete(e, params);
              }}
              sx={{
                padding: "4px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                },
              }}
              title="Delete user"
              data-testid="delete-button"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    const mappedRows =
      allUsers?.["account holders"]?.map((el, index) => {
        return {
          id: index,
          ...el,
        };
      }) || [];

    return mappedRows;
  }, [allUsers]);

  if (isLoadingMasterAdmin) {
    return <></>;
  }

  if (email && userDetails) {
    return (
      <>
        <UserDetails
          userDetails={userDetails}
          updateUserDetails={updateUserDetails}
        />
        {isModalOpen && <InviteModal />}
      </>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      <Typography
        variant="h5"
        sx={{
          borderBottom: "1px solid #E0E0E0",
          paddingBottom: 2,
          margin: "32px 0 24px 0", // Increased top margin to 32px for better spacing with new header
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: "28px",
        }}
      >
        Manage Users
      </Typography>

      <Box sx={{ height: "calc(100vh - 300px)", width: "100%", mt: 2 }}>
        <DataGrid
          headerBgColor="#F7F7F7"
          columns={columns}
          rows={rows}
          loading={isLoadingMasterAdmin}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          onRowClick={(params) => {
            navigate(
              `${constRoute.masteradminUsers}?email=${encodeURIComponent(params.row.user_email)}`,
            );
          }}
        />
      </Box>

      {isModalOpen && <InviteModal />}

      <ChangeUserPlanModal
        isOpen={openUpdatePlanModal}
        users={usersForSelect}
        userToUpdate={userToUpdate}
        typeToUpdate={typeToUpdate}
        closeModal={() => {
          setOpenUpdatePlanModal(false);
          setUserToUpdate(null);
          setIsUpgrade(null);
          setTypeToUpdate("Basic"); // Reset plan selection
        }}
        setUserToUpdate={setUserToUpdate}
        setTypeToUpdate={setTypeToUpdate}
        currentUserType={currentUserType}
        isUpgrade={isUpgrade}
        setOpenConfirmationModal={(open) => {
          if (open) {
            // Capture the current values before closing the plan modal
            const currentUserToUpdate = userToUpdate;
            const currentTypeToUpdate = typeToUpdate;
            const currentIsUpgrade = isUpgrade;

            // Close plan selection modal first
            setOpenUpdatePlanModal(false);

            // Then open confirmation modal with captured values
            setTimeout(() => {
              setUserToUpdate(currentUserToUpdate);
              setTypeToUpdate(currentTypeToUpdate);
              setIsUpgrade(currentIsUpgrade);
              setOpenConfirmationModal(true);
            }, 0);
          }
        }}
      />

      <ConfirmationModal
        isOpen={openConfirmationModal}
        closeModal={() => {
          setOpenConfirmationModal(false);
          setUserToUpdate(null);
          setIsUpgrade(null);
          setTypeToUpdate("Basic"); // Reset plan selection
        }}
        onDeleteRecord={async () => {
          if (userToUpdate && typeToUpdate) {
            // Get the user email from userToUpdate
            const email = userToUpdate.split("-")[1]?.trim();

            if (email) {
              try {
                // Update the user plan
                const updatedUsers =
                  allUsers["account holders"]?.map((el) => {
                    if (el.user_email === email) {
                      return { ...el, plan: typeToUpdate };
                    }
                    return el;
                  }) || [];

                // Call the appropriate API based on upgrade/downgrade
                if (isUpgrade) {
                  await onUpdateUserPlan({ email, new_plan: typeToUpdate });
                } else {
                  await onDowngradeUserPlan({ email, new_plan: typeToUpdate });
                }

                // Update local state
                setAllUsers({ ...allUsers, ["account holders"]: updatedUsers });
              } catch (error) {
                console.error("Error updating plan:", error);
              }
            } else {
              console.error(
                "Could not extract email from userToUpdate:",
                userToUpdate,
              );
            }

            // Close modal and reset states
            setOpenConfirmationModal(false);
            setUserToUpdate(null);
            setIsUpgrade(null);
            setTypeToUpdate("Basic");
          } else {
            console.error("Missing required data for plan change:");
            console.error("userToUpdate:", userToUpdate);
            console.error("typeToUpdate:", typeToUpdate);
          }
        }}
        userToUpdate={userToUpdate}
        typeToUpdate={typeToUpdate}
        currentUserType={currentUserType}
        isUpgrade={isUpgrade}
      />

      <DeleteUserModal
        isOpen={openDeleteModal}
        closeModal={() => {
          setOpenDeleteModal(false);
          setUserEmailToDelete(null);
        }}
        onDeleteRecord={async () => {
          if (userEmailToDelete) {
            await handleUserDelete(userEmailToDelete);
            setOpenDeleteModal(false);
            setUserEmailToDelete(null);
          }
        }}
        username={
          allUsers["account holders"]?.find(
            (el) => el.user_email === userEmailToDelete,
          )?.account_holder || "Unknown User"
        }
      />
    </Box>
  );
};

export default Users;
